import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { type ClipsListResponse } from '@air/api';
import {
  type Board,
  type BoardsListOptions,
  type Clip,
  type ListResponse,
  type SortDirection,
  type SortField,
} from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES, getPublicSearchTotalKey } from '~/constants/react-query-keys';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useCurrentBoardIdSelector } from '~/hooks/useCurrentBoardIdSelector';
import { useRegisterClipEvents } from '~/hooks/useRegisterClipEvents';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { type ClipsFetcherParams } from '~/swr-hooks/gallery/galleryView/useCollectionGalleryViewItems';
import { useGalleryViewItems } from '~/swr-hooks/gallery/galleryView/useGalleryViewItems';
import { galleryViewDataToUpdateMediaPlayer, getAssetFromAssetsData } from '~/swr-hooks/gallery/galleryView/utils';
import { defaultAssetsData, defaultBoardsData } from '~/swr-hooks/gallery/types';
import { useCanShowAssets, useCanShowBoards, useCanShowFiles } from '~/swr-hooks/gallery/useCanShow';
import { usePublicMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePublicMediaPlayerData';
import { useBoardSearchParams } from '~/swr-hooks/search/useBoardSearchParams';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { type SearchTotals } from '~/types/SearchTotals';

export type BoardChildBoardsFetcherParams = {
  cursor: string | null;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  boardsFetchParams: Partial<BoardsListOptions>;
};

export type BoardChildFetchers<B extends Board, C extends Clip> = {
  boardsFetcher: (params: BoardChildBoardsFetcherParams) => Promise<ListResponse<B>>;
  clipsFetcher: (params: ClipsFetcherParams) => Promise<ClipsListResponse<C>>;
  filesFetcher: (params: ClipsFetcherParams) => Promise<ClipsListResponse<C>>;
};

export type UseBoardChildGalleryViewItemsParams = {
  fetchers: BoardChildFetchers<Board, Clip>;
  /** Extra cache-key fields (collectionId, workspaceId). Search/sort/boardId/shortId are added here. */
  cacheKeyParams: Record<string, unknown>;
  extraEnabled?: boolean;
  shouldUpdateMediaPlayer?: boolean;
};

/**
 * Sequential boards → clips → files infinite queries for a drilled board. Prefix-neutral: callers
 * supply ShortUrl or workspace fetchers. Cache keys stay `{ ...search, shortId, boardId, ...cacheKeyParams }`.
 */
export const useBoardChildGalleryViewItems = ({
  fetchers: { boardsFetcher, clipsFetcher, filesFetcher },
  cacheKeyParams,
  extraEnabled = true,
  shouldUpdateMediaPlayer = true,
}: UseBoardChildGalleryViewItemsParams) => {
  const currentBoardId = useCurrentBoardIdSelector();
  const { shortId, presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { workspaceId } = usePublicWorkspace();
  const { updatePublicMediaPlayerData } = usePublicMediaPlayerDataUpdate(shortId);
  const { appliedSortFieldDirection: sortDirection, appliedSortFieldName: sortFieldName } = useAppliedSortField();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const queryClient = useQueryClient();

  const clipsFetchParams = useClipSearchParams();
  const boardsFetchParams = useBoardSearchParams();

  const { canShowBoards } = useCanShowBoards({ isExpandNestedDisabled });
  const { canShowFiles } = useCanShowFiles();
  const { canShowAssets } = useCanShowAssets();

  const dataCacheKey = {
    ...clipsFetchParams,
    ...boardsFetchParams,
    canShowFiles,
    canShowBoards,
    canShowAssets,
    sortFieldName,
    sortDirection,
    shortId,
    boardId: currentBoardId,
    ...cacheKeyParams,
  };

  const boardsFetcherParams = (cursor: string | null): BoardChildBoardsFetcherParams => ({
    cursor,
    sortFieldName,
    sortDirection,
    boardsFetchParams,
  });

  const clipsFetcherParams = (cursor: string | null): ClipsFetcherParams => ({
    cursor,
    sortFieldName,
    sortDirection,
    clipsFetchParams,
    expandNestedContent: true,
  });

  const {
    isLoading: areBoardsLoading,
    data: boardsData,
    fetchNextPage: loadNextBoardPage,
    isFetchingNextPage: areBoardsValidating,
    hasNextPage: hasMoreBoards,
  } = useInfiniteQuery({
    queryKey: [GALLERY_BOARDS, dataCacheKey],
    queryFn: canShowBoards
      ? ({ pageParam }) => {
          if (!currentBoardId) {
            return defaultBoardsData;
          }

          return boardsFetcher(boardsFetcherParams(pageParam || null));
        }
      : () => defaultBoardsData,
    enabled: !!currentBoardId && !!currentViewTypeName && extraEnabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const areAllBoardsLoaded = !!boardsData && !(canShowBoards && hasMoreBoards) && !!currentViewTypeName;

  const {
    data: clipsData,
    fetchNextPage: loadNextClipsPage,
    isFetchingNextPage: areClipsValidating,
    isLoading: areClipsLoading,
    hasNextPage: hasMoreClips,
  } = useInfiniteQuery({
    queryKey: [GALLERY_ASSETS, dataCacheKey],
    queryFn: canShowAssets
      ? ({ pageParam }) => {
          if (!currentBoardId) {
            return defaultAssetsData;
          }

          return clipsFetcher(clipsFetcherParams(pageParam || null));
        }
      : () => defaultAssetsData,
    enabled: ({ state: { data } }) => extraEnabled && (!data || (areAllBoardsLoaded && !!currentBoardId)),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  useRegisterClipEvents({
    clips: getAssetFromAssetsData(clipsData?.pages),
    workspaceId,
  });

  const areAllClipsLoaded = !!clipsData && !(canShowAssets && hasMoreClips);

  const {
    data: filesData,
    fetchNextPage: loadNextFilesPage,
    isFetchingNextPage: areFilesValidating,
    isLoading: areFilesLoading,
    hasNextPage: hasMoreFiles,
  } = useInfiniteQuery({
    queryKey: [GALLERY_FILES, dataCacheKey],
    queryFn: canShowFiles
      ? ({ pageParam }) => {
          if (!currentBoardId) {
            return defaultAssetsData;
          }

          return filesFetcher(clipsFetcherParams(pageParam || null));
        }
      : () => defaultAssetsData,
    enabled: ({ state: { data } }) =>
      extraEnabled && (!data || (areAllBoardsLoaded && areAllClipsLoaded && !!currentBoardId)),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const itemsData = useGalleryViewItems({
    boards: {
      isValidating: areBoardsLoading || areBoardsValidating,
      data: canShowBoards ? (boardsData?.pages ?? []) : [],
      loadNextPage: loadNextBoardPage,
      isVisible: canShowBoards,
      hasMore: canShowBoards && !!hasMoreBoards,
    },
    clips: {
      isValidating: areClipsLoading || areClipsValidating,
      data: clipsData?.pages ?? [],
      loadNextPage: loadNextClipsPage,
      isVisible: canShowAssets,
      hasMore: !!hasMoreClips,
    },
    files: {
      isValidating: areFilesLoading || areFilesValidating,
      data: filesData?.pages ?? [],
      loadNextPage: loadNextFilesPage,
      isVisible: canShowFiles,
      hasMore: !!hasMoreFiles,
    },
  });

  useEffect(() => {
    if (shouldUpdateMediaPlayer) {
      updatePublicMediaPlayerData(galleryViewDataToUpdateMediaPlayer(itemsData));
    }
  }, [itemsData, updatePublicMediaPlayerData, shouldUpdateMediaPlayer]);

  useEffect(() => {
    queryClient.setQueryData<SearchTotals>(getPublicSearchTotalKey(shortId), {
      total:
        (canShowBoards ? itemsData.data.boards.total : 0) + itemsData.data.clips.total + itemsData.data.files.total,
    });
  }, [canShowBoards, itemsData, queryClient, shortId]);

  return itemsData;
};
